import React, { useState } from "react";
import { Trash2, Plus, Play } from "lucide-react";
import BlogTitleForm from "./BlogTitleForm";

const BlogTitlesTable = ({
    blogTitles,
    isLoading,
    onDelete,
    onAdd,
    onProcessAll,
}) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    if (isLoading) {
        return <div className="text-center py-4">Loading...</div>;
    }

    const handleAddSubmit = (formData) => {
        onAdd(formData);
        setShowAddForm(false);
    };

    const handleProcessAll = async () => {
        setIsProcessing(true);
        try {
            await onProcessAll();
        } finally {
            setIsProcessing(false);
        }
    };

    const pendingCount =
        blogTitles?.filter((title) => title.status === "Pending").length || 0;

    if (!blogTitles?.length) {
        return (
            <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    No blog titles found
                </h3>
            </div>
        );
    }

    return (
        <div>
            <div className="mb-4 flex items-center space-x-3">
                <div className="flex space-x-2">
                    <button
                        onClick={handleProcessAll}
                        disabled={isProcessing || pendingCount === 0}
                        className={`inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors duration-150 ${
                            isProcessing || pendingCount === 0
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        }`}
                    >
                        <Play className="h-4 w-4 mr-1.5" />
                        {isProcessing
                            ? "Processing..."
                            : `Process All (${pendingCount})`}
                    </button>
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                    >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add New
                    </button>
                </div>
            </div>

            {showAddForm && (
                <BlogTitleForm
                    onSubmit={handleAddSubmit}
                    onCancel={() => setShowAddForm(false)}
                />
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                                Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {blogTitles.map((title) => (
                            <tr key={title.id}>
                                <td className="px-6 py-4">
                                    <div className="max-w-md">
                                        <div className="truncate hover:overflow-visible hover:whitespace-normal hover:bg-gray-50 hover:absolute hover:z-10 hover:shadow-lg hover:p-2 hover:rounded hover:border hover:border-gray-200">
                                            {title.title}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                            title.status === "Processed"
                                                ? "bg-green-100 text-green-800"
                                                : title.status === "Pending"
                                                ? "bg-yellow-100 text-yellow-800"
                                                : "bg-red-100 text-red-800"
                                        }`}
                                    >
                                        {title.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                        onClick={() => onDelete(title.id)}
                                        className="text-red-600 hover:text-red-900"
                                        title="Delete blog title"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BlogTitlesTable;
